import {
  List,
  Datagrid,
  FunctionField,
  SelectInput,
  RaRecord,
} from 'react-admin';
import { Chip, Typography } from '@mui/material';
import { DateTimeCell, InlinePair, compactDatagridSx } from '../../shared/listCells';
import { menuLabel } from '../menuConfig';
import { auditDetailsText, auditSubjectText } from './auditSubject';

type LooseRecord = RaRecord & Record<string, unknown>;

const ACTION_CHOICES = [
  { id: 'utworzenie', name: 'Utworzenie' },
  { id: 'zmiana', name: 'Zmiana' },
  { id: 'usuniecie', name: 'Usunięcie' },
];

const filters = [
  <SelectInput source="action" label="Działanie" choices={ACTION_CHOICES} alwaysOn />,
];

/** Rodzaj działania — usunięcie wyróżnione, bo jest nieodwracalne. */
const AuditAction = ({ record }: { record: LooseRecord }) => {
  const a = String(record.action ?? '');
  const label = ACTION_CHOICES.find((d) => d.id === a)?.name ?? a;
  return (
    <Chip
      size="small"
      variant={a === 'usuniecie' ? 'filled' : 'outlined'}
      color={a === 'usuniecie' ? 'error' : a === 'utworzenie' ? 'success' : 'default'}
      label={label}
    />
  );
};

/**
 * Kto wykonał działanie.
 *
 * Pokazujemy zapamiętaną nazwę, nie dociągamy konta — sprawca mógł zostać
 * usunięty, a wpis ma zostać czytelny także wtedy.
 */
const ActorCell = ({ record }: { record: LooseRecord }) => {
  const actor = record.actorName;
  if (!actor) {
    return (
      <Typography variant="caption" color="text.secondary">
        {record.actorId ? `konto nr ${String(record.actorId)}` : 'nieznany'}
      </Typography>
    );
  }
  return (
    <InlinePair
      primary={String(actor)}
      secondary={record.ipAddress != null ? String(record.ipAddress) : null}
      bold={false}
      maxWidth={{ xs: 260, xl: 340 }}
    />
  );
};

/**
 * Czego dotyczyło — przedmiot (nazwą, nie kodem zasobu; `auditSubject.ts`)
 * z numerem i opisem zmiany w jednej linii.
 * Opis bywa długi, więc to on się skraca; pełny jest w podpowiedzi.
 */
const SubjectCell = ({ record }: { record: LooseRecord }) => (
  <InlinePair
    primary={auditSubjectText(record)}
    secondary={auditDetailsText(record)}
    bold={false}
    shrink="secondary"
    maxWidth={{ xs: 560, xl: 900 }}
  />
);

/**
 * Dziennik działań administracyjnych — tylko odczyt.
 *
 * Bez przycisków tworzenia i usuwania świadomie: dziennik, do którego da się
 * dopisać albo z którego da się skasować, przestaje być dowodem.
 */
export const AuditLogList = () => (
  <List
    filters={filters}
    perPage={25}
    title={menuLabel('admin-log')}
    sort={{ field: 'createdAt', order: 'DESC' }}
  >
    <Datagrid bulkActionButtons={false} rowClick={false} sx={compactDatagridSx}>
      {/* Sekundy w podpowiedzi, nie w wierszu — dziennik jest dowodem, więc
          pełnego czasu nie wyrzucamy. `source` zostaje: po nim się sortuje. */}
      <FunctionField
        source="createdAt"
        label="Kiedy"
        render={(r: RaRecord) => <DateTimeCell value={r.createdAt as string | undefined} />}
      />
      <FunctionField label="Działanie" render={(r: RaRecord) => <AuditAction record={r} />} />
      <FunctionField label="Kto" render={(r: RaRecord) => <ActorCell record={r} />} />
      <FunctionField label="Czego dotyczyło" render={(r: RaRecord) => <SubjectCell record={r} />} />
    </Datagrid>
  </List>
);
